import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { api, errMsg } from "@/lib/api";
import { TOOL_LIST } from "@/lib/tools";
import { HistoryList } from "@/components/HistoryList";
import { CreditAlert, GoogleOnlyAlert } from "@/components/CreditAlert";

const CreditsCard = ({ credits, unlimited }) => {
  const { t, href } = useLang();
  return (
    <div className="relative overflow-hidden rounded-2xl bg-slate-900 p-8 text-white md:col-span-5">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#00A3E0] opacity-30 blur-2xl" />
      <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{t("dash.balance")}</p>
      <p className="mt-3 font-display text-6xl font-bold" data-testid="dashboard-credit-balance">{credits}</p>
      <p className="mt-1 text-sm text-slate-300">{t("dash.perFile")}</p>
      {!unlimited && (
        <Button asChild className="mt-8 rounded-full bg-white text-slate-900 hover:bg-slate-100" data-testid="dashboard-buy-credits-button">
          <Link to={href("pricing")}><Coins className="mr-2 h-4 w-4" />{t("dash.buy")}</Link>
        </Button>
      )}
    </div>
  );
};

const QuickTools = () => {
  const { t, toolHref } = useLang();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:col-span-7">
      {TOOL_LIST.map((tl) => {
        const Icon = tl.icon;
        return (
          <Link key={tl.slug} to={toolHref(tl.slug)} data-testid={`dashboard-tool-${tl.slug}`} className="group flex min-w-0 items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-slate-900">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-white" style={{ background: tl.color }}><Icon className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-slate-900">{t(`tools.${tl.slug}.name`)}</span>
              <span className="block truncate text-xs text-slate-500">{t(`tools.${tl.slug}.short`)}</span>
            </span>
            <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-slate-900" />
          </Link>
        );
      })}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t, tErr } = useLang();
  const [jobs, setJobs] = useState([]);
  const [pending, setPending] = useState(null);
  const load = useCallback(() => api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const del = async () => {
    const id = pending.job_id;
    setPending(null);
    try { await api.delete(`/jobs/${id}`); setJobs((j) => j.filter((x) => x.job_id !== id)); toast.success(t("dash.removed")); }
    catch (e) { toast.error(tErr(errMsg(e))); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <p className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("dash.eyebrow")}</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("dash.hello", { name: user.name?.split(" ")[0] })}</h1>
      <div className="mt-8 space-y-3">
        <GoogleOnlyAlert user={user} />
        <CreditAlert user={user} />
      </div>
      <div className="mt-10 grid gap-5 md:grid-cols-12">
        <CreditsCard credits={user.unlimited ? "∞" : user.credits} unlimited={user.unlimited} />
        <QuickTools />
      </div>
      <div className="mt-14 flex items-end justify-between">
        <h2 className="text-xl font-semibold text-slate-900">{t("dash.history")}</h2>
        <span className="font-mono text-xs text-slate-400">{t("dash.files", { n: jobs.length })}</span>
      </div>
      <div className="mt-5"><HistoryList jobs={jobs} onDelete={setPending} /></div>
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dash.confirmText", { name: pending?.input_name })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">{t("dash.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-button">{t("dash.remove")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
